/**
 * Shared presentation logic for reading goals. Every figure a goal card shows
 * comes from the API's derived `progress` block; these helpers only format.
 */

export const GOAL_TYPES = {
  deadline: {
    label: "Finish an order by a date",
    description: "Before a film, or any deadline",
  },
  rate: {
    label: "Read a set amount regularly",
    description: "Per day, week or month",
  },
  yearly: {
    label: "Yearly challenge",
    description: "A total for the year",
  },
};

export const WEEKDAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const TYPE_ORDER = { deadline: 0, rate: 1, yearly: 2 };

const NUDGE_STORAGE_PREFIX = "@GoalNudge:";
const NUDGE_INTERVAL_MS = 7 * 24 * 60 * 60 * 1000;

const plural = (count, singular, pluralWord = `${singular}s`) =>
  Number(count) === 1 ? singular : pluralWord;

const LESS_THAN_ONE = "less than 1";

/**
 * Comic counts are never shown as a fraction of a comic: anything between
 * zero and one reads as "less than 1".
 */
const trimNumber = (value) => {
  const number = Number(value) || 0;
  if (number > 0 && number < 1) {
    return LESS_THAN_ONE;
  }
  if (number >= 10) {
    return String(Math.round(number));
  }
  return String(Math.round(number * 10) / 10);
};

/**
 * Time is always an approximation: "about 80 minutes", "about 2h 10m".
 */
export const formatMinutes = (minutes) => {
  const total = Math.max(Math.round(Number(minutes) || 0), 0);
  if (total < 120) {
    return `about ${total} ${plural(total, "minute")}`;
  }
  const hours = Math.floor(total / 60);
  const rest = total % 60;
  return rest ? `about ${hours}h ${rest}m` : `about ${hours}h`;
};

/**
 * Compact variant for figure rows and subtitles: "80 min", "2h 10m".
 */
export const formatMinutesShort = (minutes) => {
  const total = Math.max(Math.round(Number(minutes) || 0), 0);
  if (total < 120) {
    return `${total} min`;
  }
  const hours = Math.floor(total / 60);
  const rest = total % 60;
  return rest ? `${hours}h ${rest}m` : `${hours}h`;
};

export const formatPerDay = (perDay) => `${trimNumber(perDay)}/day`;

export const RATE_PERIODS = ["day", "week", "month"];

export const RATE_QUICK_PICKS = {
  day: [1, 2, 3, 5],
  week: [2, 5, 7, 14],
  month: [10, 20, 30, 60],
};
export const DEFAULT_RATE_TARGET = { day: 1, week: 5, month: 20 };

/** Yearly challenge quick picks, each with the pace it works out to. */
export const YEARLY_QUICK_PICKS = [
  { value: 52, caption: "one a week" },
  { value: 104, caption: "two a week" },
  { value: 250, caption: "five a week" },
  { value: 365, caption: "one a day" },
];
export const DEFAULT_YEARLY_TARGET = 250;

export const rateResetsLabel = (period, resetDay) => {
  if (period === "week") {
    return `Every ${WEEKDAYS[Number(resetDay) || 0] || "Monday"}`;
  }
  if (period === "month") {
    return "1st of the month";
  }
  return "Midnight";
};

/**
 * "About 1h 40m a week" in the short form used by the rate form, with
 * "About 15 minutes a day" when under an hour.
 */
const aboutTime = (minutes) => {
  const total = Math.max(Math.round(Number(minutes) || 0), 0);
  if (total < 60) {
    return `About ${total} ${plural(total, "minute")}`;
  }
  const hours = Math.floor(total / 60);
  const rest = total % 60;
  return rest ? `About ${hours}h ${rest}m` : `About ${hours}h`;
};

/**
 * The info box under the rate stepper: the time the target costs per period,
 * and one sentence that spreads it out so the number is easy to judge.
 */
export const rateInfoCopy = (target, period, minutesPerIssue, periodDays) => {
  const count = Math.max(Number(target) || 0, 0);
  const perIssue = Number(minutesPerIssue) || 15;
  const total = count * perIssue;
  const days =
    Number(periodDays) || (period === "week" ? 7 : period === "month" ? 30 : 1);
  const unit =
    period === "month" ? "month" : period === "week" ? "week" : "day";
  const headline = `${aboutTime(total)} a ${unit}`;

  if (period === "week") {
    const perDay = Math.round(total / 7);
    const sittings = Math.max(1, Math.ceil(total / 60));
    return {
      headline,
      detail: `Roughly ${perDay} ${plural(
        perDay,
        "minute"
      )} a day, or ${sittings} ${plural(sittings, "sitting")} at the weekend.`,
    };
  }
  if (period === "month") {
    const perWeek = Math.max(Math.round(count / 4.3), 1);
    const perDay = Math.round(total / days);
    return {
      headline,
      detail: `Roughly ${perWeek} a week, or ${perDay} ${plural(
        perDay,
        "minute"
      )} a day.`,
    };
  }
  return {
    headline,
    detail: `Roughly ${count * 7} a week.`,
  };
};

export const formatCount = (value) => trimNumber(value);

/**
 * "3 Oct" from an ISO date (YYYY-MM-DD or timestamp).
 */
export const formatShortDate = (value) => {
  if (!value) {
    return "";
  }
  const iso = String(value);
  const date = /^\d{4}-\d{2}-\d{2}$/.test(iso)
    ? new Date(`${iso}T12:00:00`)
    : new Date(iso);
  if (!Number.isFinite(date.getTime())) {
    return "";
  }
  return `${date.getDate()} ${MONTHS[date.getMonth()]}`;
};

/**
 * Status chip. Never a failure state: behind is amber, everything else is
 * green or neutral.
 */
export const goalChip = (goal) => {
  const progress = goal?.progress || {};
  if (goal?.status === "paused") {
    return { label: "Paused", colorScheme: "gray" };
  }
  if (goal?.status === "completed") {
    return { label: "Done", colorScheme: "green" };
  }
  if (goal?.status === "cancelled") {
    return { label: "Ended", colorScheme: "gray" };
  }
  if (goal?.status === "expired" || progress.paceStatus === "expired") {
    return { label: "Deadline passed", colorScheme: "gray" };
  }
  const delta = Math.abs(Number(progress.delta) || 0);
  switch (progress.paceStatus) {
    case "done":
      return { label: "Done", colorScheme: "green" };
    case "behind":
      return { label: `${delta} behind`, colorScheme: "yellow" };
    case "ahead":
      return { label: `${delta} ahead`, colorScheme: "green" };
    case "on_track":
      return { label: "On track", colorScheme: "green" };
    default:
      return null;
  }
};

/**
 * "before the film · 12 days left" or "by 28 Sep · 80 min/day".
 */
export const deadlineSubtitle = (goal) => {
  const progress = goal?.progress || {};
  if (goal?.status === "paused") {
    return `Paused since ${formatShortDate(goal.pausedAt)}`;
  }
  if (!goal?.endsOn) {
    return "No deadline · keep reading";
  }
  const daysLeft = Number(progress.daysRemaining);
  if (goal.status === "expired" || progress.paceStatus === "expired") {
    return `Deadline passed on ${formatShortDate(goal.endsOn)}`;
  }
  if (goal.source === "film_release") {
    return `before the film · ${daysLeft} ${plural(daysLeft, "day")} left`;
  }
  const minutes = progress.requiredMinutesPerDay;
  return `by ${formatShortDate(goal.endsOn)}${
    minutes !== null && minutes !== undefined
      ? ` · ${formatMinutesShort(minutes)}/day`
      : ""
  }`;
};

/**
 * "3 of 5 · resets Monday"
 */
export const rateFooter = (goal) => {
  const progress = goal?.progress || {};
  if (goal?.status === "paused") {
    return `Paused since ${formatShortDate(goal.pausedAt)}`;
  }
  const completed = Math.min(
    Number(progress.completed) || 0,
    Number(progress.target) || 0
  );
  return `${completed} of ${progress.target ?? goal?.targetCount} · ${
    progress.resetsLabel || "resets at midnight"
  }`;
};

/**
 * "11 ahead of schedule" / "9 behind schedule" / "On schedule".
 */
export const yearlyLine = (goal) => {
  const progress = goal?.progress || {};
  if (goal?.status === "paused") {
    return `Paused since ${formatShortDate(goal.pausedAt)}`;
  }
  const delta = Number(progress.delta) || 0;
  if (progress.paceStatus === "done") {
    return "Challenge complete";
  }
  if (delta > 0) {
    return `${delta} ahead of schedule`;
  }
  if (delta < 0) {
    return `${Math.abs(delta)} behind schedule`;
  }
  return "On schedule";
};

/**
 * Projected finish: "3 Oct" or "not started" when there is no recent pace.
 */
export const projectedFinishLabel = (progress) =>
  progress?.projectedFinish
    ? formatShortDate(progress.projectedFinish)
    : "not started";

/**
 * Feasibility copy. Headline + detail; the comparison sentence is skipped
 * for users with no reading history.
 */
export const feasibilityCopy = (feasibility, type) => {
  if (!feasibility) {
    return null;
  }
  const perDay = Number(feasibility.requiredPerDay) || 0;
  const perDayLabel = trimNumber(perDay);
  const headline =
    type === "rate" && feasibility.requiredPerDay < 1
      ? `That's about ${formatMinutesShort(
          feasibility.requiredMinutesPerDay
        )} a day`
      : `That's ${perDayLabel} ${plural(
          perDayLabel === LESS_THAN_ONE ? 1 : perDayLabel,
          "comic"
        )} a day`;
  const sentences = [
    `About ${Math.max(
      Math.round(Number(feasibility.requiredMinutesPerDay) || 0),
      0
    )} minutes of reading daily.`,
  ];
  if (feasibility.hasHistory) {
    const average = trimNumber(feasibility.recentDailyAverage);
    sentences.push(`You've averaged ${average} a day this month.`);
  }
  if (feasibility.premiumNote) {
    sentences.push(feasibility.premiumNote);
  }
  return { headline, detail: sentences.join(" ") };
};

/**
 * Deadline goals first, then rate, then yearly.
 */
export const sortActiveGoals = (goals = []) =>
  [...goals].sort((a, b) => {
    const order = (TYPE_ORDER[a.type] ?? 9) - (TYPE_ORDER[b.type] ?? 9);
    return order !== 0 ? order : (a.id || 0) - (b.id || 0);
  });

export const combinedLoadCopy = (load) => {
  if (!load) {
    return null;
  }
  return {
    headline: `Your goals need ${formatMinutesShort(
      load.requiredMinutesPerDay
    )} a day`,
    detail: load.hasHistory
      ? `You've averaged ${formatMinutesShort(
          load.averageMinutesPerDay
        )} a day this month`
      : "Set a pace you can keep",
  };
};

/**
 * The inline "Move to 3 Oct?" prompt is shown at most once a week per goal.
 */
export const shouldShowNudge = (goalId, now = Date.now()) => {
  try {
    const raw = window.localStorage.getItem(`${NUDGE_STORAGE_PREFIX}${goalId}`);
    if (!raw) {
      return true;
    }
    return now - Number(raw) >= NUDGE_INTERVAL_MS;
  } catch (error) {
    return true;
  }
};

export const dismissNudge = (goalId, now = Date.now()) => {
  try {
    window.localStorage.setItem(
      `${NUDGE_STORAGE_PREFIX}${goalId}`,
      String(now)
    );
  } catch (error) {
    // Browser storage unavailable: the prompt simply shows again next time.
  }
};

export const todayIso = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
};

export const addMonthsIso = (months, from = new Date()) => {
  const date = new Date(from);
  date.setMonth(date.getMonth() + months);
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

export const addDaysIso = (days, from = new Date()) => {
  const date = new Date(from);
  date.setDate(date.getDate() + days);
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

/**
 * Inline goal line for the reading orders list: "12 days left · 4/day".
 */
export const readingGoalLabel = (goal) => {
  if (!goal) {
    return null;
  }
  const progress = goal.progress || {};
  if (goal.status === "paused") {
    return "Goal paused";
  }
  const parts = [];
  if (progress.daysRemaining !== null && progress.daysRemaining !== undefined) {
    parts.push(
      `${progress.daysRemaining} ${plural(progress.daysRemaining, "day")} left`
    );
  }
  if (
    progress.requiredPerDay !== null &&
    progress.requiredPerDay !== undefined
  ) {
    parts.push(formatPerDay(progress.requiredPerDay));
  }
  return parts.length ? `Goal: ${parts.join(" · ")}` : "Goal set";
};

/**
 * "Rate goal: 3 of 5 this week"
 */
export const rateGoalLink = (goal) => {
  if (!goal) {
    return null;
  }
  const progress = goal.progress || {};
  const completed = Math.min(
    Number(progress.completed) || 0,
    Number(progress.target) || goal.targetCount
  );
  return `Rate goal: ${completed} of ${progress.target ?? goal.targetCount} ${
    goal.period === "week" ? "this week" : "today"
  }`;
};

export const findLiveDeadlineGoal = (goals = [], readingOrderId) =>
  goals.find(
    (goal) =>
      goal.type === "deadline" &&
      String(goal.readingOrderId) === String(readingOrderId)
  ) || null;

export const findLiveRateGoal = (goals = []) =>
  goals.find((goal) => goal.type === "rate") || null;
